
import * as React from 'react';
import { Typography, List, ListItem, ListItemText, ListItemIcon, Button, ListItemButton } from '@mui/material';
import { Box, Paper, Divider, IconButton } from '@mui/material';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import ProcedureSelect from '../modals/ProcedureSelect';
import { usePetHistoryEntries } from '../../../hooks/history/usePetHistoryEntries';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { formatToLocalDateTime } from '../../../utils/dateFormatter';
import ProcedureView from '../modals/ProcedureView';
import { useCreateHistoryEntry } from '../../../hooks/history/useCreateHistoryEntry';
import { getHistoryEntryById } from '../../../api/historyService';
import { useDeleteHistoryEntry } from '../../../hooks/history/useDeleteHistoryEntry';
import CircularProgress from '@mui/material/CircularProgress';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RequirePermission from '../../RequirePermission';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';

export default function ProceduresList({ 
  label = "Procedures",
  refreshDocumentationList, 
  history = null, 
  pet = null, 
  mode = 'historyId', 
  border = true, 
  showAddButton = true,
  fetchData = null
}) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const [listEmpty, setListEmpty] = React.useState(true);
  const [procedureViewOpen, setProcedureViewOpen] = React.useState(false);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = React.useState(null);

  const { historyEntries, loading, loadMore, resetParent } = usePetHistoryEntries(history?.id, pet?.id, mode);

  const onCreateHistoryEntrySuccess = async (newHistoryEntry) => {
    resetParent();
    setSelectedHistoryEntry(await getHistoryEntryById(newHistoryEntry.id));
    setProcedureViewOpen(true);
  };

  const { create } = useCreateHistoryEntry(onCreateHistoryEntrySuccess);

  const [procedureSelectOpen, setProcedureSelectOpen] = React.useState(false);
  const onProcedureSelect = async (selectedTypeId) => {
    await create({ history_id: history.id, type_id: selectedTypeId });
    setProcedureSelectOpen(false);
  };

  const handleOpen = (historyEntry) => {
    setSelectedHistoryEntry(historyEntry);
    setProcedureViewOpen(true);
  };

  const onDeleteSuccess = () => {
    resetParent();
    showSnackbar(t('procedures.messages.delete_success'), "success");
  };

  const { remove } = useDeleteHistoryEntry(onDeleteSuccess);

  const handleDelete = async (historyEntry) => {
    try {
      await remove(historyEntry.id);
    } catch (error) {
      if (error.status === 409) {
        showSnackbar(t("procedures.messages.delete_fail_has_entries"), "error");
      } else {
        showSnackbar(t('procedures.messages.delete_fail'), "error");
      }
    }
  };

  const content = (
    <>
      <Typography variant="h6">{label}</Typography>
      <br />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {historyEntries.data.length === 0 ? (
            <Divider variant="fullWidth" component="li">
              <Typography variant="caption" color="text.secondary">
                no procedures
              </Typography>
            </Divider>
          ) : null}
          <Box sx={{
            maxHeight: 300,
            overflow: "auto"
          }}>
          <List sx={{ mb: 1, width: '100%', bgcolor: 'background.paper' }}>
            {historyEntries?.data?.map((historyEntry) => (
              <React.Fragment key={historyEntry.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <VerticalHamburgerMenu
                        options={[
                          {
                            id: 1,
                            name: 'Open',
                            callback: () => handleOpen(historyEntry),
                            disabled: !userHasAccess(SUB_MODULES.PROCEDURES, 1)
                          },
                          {
                            id: 2,
                            name: 'Delete',
                            callback: () => handleDelete(historyEntry),
                            confirmMessage: t('procedures.messages.delete_confirm', { name: historyEntry.type.name }),
                            disabled: !userHasAccess(SUB_MODULES.PROCEDURES, 3)
                          },
                        ]}
                      />
                    </Box>
                  }
                >
                  <ListItemButton sx={{ py: 0 }} onClick={() => handleOpen(historyEntry)}>
                    <ListItemIcon>
                      <MedicalServicesIcon fontSize="medium" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{ whiteSpace: 'pre-line', maxWidth: '90%' }}
                        >
                          {historyEntry.type.name}
                        </Typography>
                      }
                      secondary={
                        <span style={{ display: 'block', textAlign: 'left' }}>
                          {formatToLocalDateTime(historyEntry.datetime)}
                        </span>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          {/* Load More only shows if more pages exist */}
          {historyEntries?.pagination?.page < historyEntries?.pagination?.totalPages && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <IconButton 
                  onClick={loadMore}
                  sx={{
                      width: '100%',              // fill container width
                      justifyContent: 'center',   // center the icon horizontally
                      color: 'primary.main',      // use theme's primary color
                  }}
              >
                  <KeyboardArrowDownIcon />
              </IconButton>
            </Box>
          )}
          </Box>
        </>
      )}

      {showAddButton ? 
      <RequirePermission subModuleName={SUB_MODULES.PROCEDURES} minAccessLevel={2}>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setProcedureSelectOpen(true)}
          startIcon={<LocalHospitalIcon />}
        >
          Add Procedure
        </Button>
      </RequirePermission>
      : null}
      
      <ProcedureSelect
        open={procedureSelectOpen}
        onClose={() => setProcedureSelectOpen(false)}
        onSelect={onProcedureSelect}
      />

      <ProcedureView
        refreshDocumentationList={refreshDocumentationList}
        open={procedureViewOpen}
        onClose={() => {
          setProcedureViewOpen(false);
          fetchData();
        }}
        historyEntry={selectedHistoryEntry}
      />
    </>
  );

  return border ? (
    <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
      {content}
    </Paper>
  ) : (
    content
  );
}
